
import {
    Canvas, Circle, FabricImage, FabricObject,
    FabricText, Group, Line, Point, Rect, Textbox, Triangle
} from "fabric";

Object.assign(FabricObject.ownDefaults, {
    evented: false,
    selectable: false,
    hasControls: false,
    hoverCursor: "default",
    borderScaleFactor: 1.8,
    cornerColor: "#5AB248",
    borderColor: "#5AB248",
    cornerStrokeColor: "#5AB248",
});

export class Plot {

    constructor(canvasId, id) {
        this.canvasId = canvasId;
        this.id = id;
        this.leftClick = 1;
        this.rightClick = 3;
        this.refScale = 0.01;
        this.canvas = new Canvas(canvasId, {
            preserveObjectStacking: true,
            backgroundColor: "#fff",
            stopContextMenu: true,
            fireRightClick: true,
            selection: false,
            // Only used by objects with perPixelTargetFind (user-drawn lines), so thin lines stay clickable.
            targetFindTolerance: 5,
        });
        this.layers = new Map();

        //workarround for object stacking
        Array.from({ length: 1000 }).forEach(() => {
            this.canvas.add(new Circle({ visible: false }));
        });
        this.addRestoreHandler();
        this.addZoomHandler();
        this.addPanHandler();
        this.addResizeHandler();
    }

    addRestoreHandler = () => {
        const key = this.canvasId + "-viewport-" + this.id;
        const viewport = JSON.parse(localStorage.getItem(key));
        if (viewport) {
            this.canvas.setViewportTransform(viewport);
        } else {
            const canvasHeight = this.canvas.getHeight();
            const transform = [1, 0, 0, 1, 5, canvasHeight - 5];
            this.canvas.setViewportTransform(transform);
        }
        const saveViewport = () => {
            if (this.id) {
                const key = this.canvasId + "-viewport-" + this.id;
                const value = JSON.stringify(this.canvas.viewportTransform);
                localStorage.setItem(key, value);
            }
        };
        this.canvas.on("mouse:up", () => saveViewport());
        this.canvas.on("mouse:wheel", () => saveViewport());
    };

    addResizeHandler = () => {
        const container = document.getElementById(this.canvasId).parentElement.parentElement;
        const resizePlot = () => {
            if (!this.canvas.lowerCanvasEl) {return;}
            const rect = container.getBoundingClientRect();
            this.canvas.setDimensions({ width: rect.width, height: rect.height });
            this.canvas.fire("viewport");
            this.canvas.renderAll();
        };
        this.resizeObserver = new ResizeObserver(() => {
            resizePlot();
        });
        this.resizeObserver.observe(container);
    };

    addZoomHandler = () => {
        const zoomMouseWheel = opt => {
            const delta = opt.e.deltaY;
            let zoom = this.canvas.getZoom();
            zoom *= 0.999 ** delta;
            if (zoom > 25) {
                zoom = 25;
            }
            if (zoom < 0.01) {
                zoom = 0.01;
            }
            this.canvas.fire("rescale");
            this.canvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);
            this.canvas.fire("viewport");
            opt.e.preventDefault();
            opt.e.stopPropagation();
        };
        this.canvas.on("mouse:wheel", zoomMouseWheel);
    };

    addPanHandler = () => {
        const panMouseDown = (opt, obj) => {
            if (opt.e.which !== this.rightClick) {
                return;
            }
            obj.isDragging = true;
            obj.lastPosX = opt.e.clientX;
            obj.lastPosY = opt.e.clientY;
        };
        const panMouseMove = (opt, obj) => {
            if (!obj.isDragging) {
                return;
            }
            const vpt = this.canvas.viewportTransform;
            vpt[4] += opt.e.clientX - obj.lastPosX;
            vpt[5] += opt.e.clientY - obj.lastPosY;
            if (this.canvas.getActiveObject()) {
                this.canvas.getActiveObject().setCoords();
            }
            this.canvas.requestRenderAll();
            obj.lastPosX = opt.e.clientX;
            obj.lastPosY = opt.e.clientY;
            this.canvas.fire("viewport");
        };
        const panMouseUp = (opt, obj) => {
            Object.keys(obj).forEach(i => (obj[i] = undefined));
        };
        const obj = {};
        this.canvas.on("mouse:down", opt => panMouseDown(opt, obj));
        this.canvas.on("mouse:move", opt => panMouseMove(opt, obj));
        this.canvas.on("mouse:up", opt => panMouseUp(opt, obj));
    };

    addLayer = (name, index) => {
        for (const layerName of this.layers.keys()) {
            if (layerName === name) {
                return this.layers.get(name);
            }
        }
        const layer = {
            name,
            active: false,
            objects: [],
            handlers: new Map(),
            deactivationHandler: undefined,
            getObjects: () => [...layer.objects],
            addObject: (object, idx = 0) => {
                if (!object.nonInteractive) {
                    object.evented = layer.active;
                    object.selectable = layer.active;
                }
                layer.objects.push(object);
                this.canvas.insertAt(index + idx + layer.objects.length, object);
            },
            removeObject: object => {
                if (object.type === "group") {
                    object.forEachObject(layer.removeObject);
                }
                const objectIndex = layer.getObjects().indexOf(object);
                if (objectIndex > -1) {
                    layer.objects.splice(objectIndex, 1);
                }
                this.canvas.remove(object);
            },
            removeObjects: () => layer.getObjects().forEach(layer.removeObject),
            removeHandler: eventName => {
                const existingHandler = layer.handlers.get(eventName);
                if (existingHandler) {
                    this.canvas.off(eventName, existingHandler);
                    layer.handlers.set(eventName, undefined);
                }
            },
            removeHandlers: () => {
                layer.handlers.forEach((handler, eventName) => layer.removeHandler(eventName));
                layer.handlers.clear();
            },
            setHandler: (eventName, handler) => {
                layer.removeHandler(eventName);
                layer.handlers.set(eventName, handler);
                if (layer.active) {
                    this.canvas.on(eventName, handler);
                }
            },
            deactivate: (l = layer) => {
                l.active = false;
                l.deactivationHandler?.(layer);
                l.objects.forEach(obj => obj.set({ evented: false, selectable: false }));
                l.handlers.forEach((handler, eventName) => this.canvas.off(eventName, handler));
                this.canvas.discardActiveObject();
            },
            activate: () => {
                this.canvas.discardActiveObject();
                this.layers.forEach(layer.deactivate);
                layer.active = true;
                layer.objects.filter(obj => !obj.nonInteractive).forEach(obj => obj.set({ evented: true, selectable: true }));
                layer.handlers.forEach((handler, eventName) => layer.setHandler(eventName, handler));
                this.canvas.renderAll();
            },
            onDeactivate: func => {
                layer.deactivationHandler = func;
            },
            bringObjectToFront: object => {
                const layerObjects = layer.getObjects();
                if (!layerObjects.length) {
                    return;
                }
                const canvasObjects = this.canvas.getObjects();
                const layerCanvasIndices = layerObjects
                    .map(o => canvasObjects.indexOf(o))
                    .filter(i => i >= 0)
                    .sort((a, b) => a - b);
                if (!layerCanvasIndices.length) {
                    return;
                }
                const objectCanvasIndex = canvasObjects.indexOf(object);
                if (objectCanvasIndex < 0) {
                    return;
                }
                const maxLayerIndex = layerCanvasIndices[layerCanvasIndices.length - 1];
                if (objectCanvasIndex === maxLayerIndex) {
                    return;
                }
                // move inside layer.objects to front (end)
                const objectLayerIndex = layer.objects.indexOf(object);
                if (objectLayerIndex < 0) {
                    return;
                }
                layer.objects.splice(objectLayerIndex, 1);
                layer.objects.push(object);
                this.canvas.moveObjectTo(object, maxLayerIndex);
            }
        };
        this.layers.set(name, layer);
        return layer;
    };

    getLayer = name => this.layers.get(name);

    applyArrowFeature = layer => {
        const feature = {
            type: "arrow-feature",
            onSelect: func => {
                layer.setHandler("selection:created", opt => func(opt.selected[0]));
                layer.setHandler("selection:updated", opt => func(opt.selected[0]));
                layer.setHandler("selection:cleared", () => func(undefined));
            },
            onModify: func => {
                layer.setHandler("object:modified", opt => func(opt.target));
            },
            onPlace: (arrow, func) => {
                layer.setHandler("mouse:down", opt => {
                    if (opt.e.which !== this.leftClick
                        || opt?.target?.type === "arrow"
                        || !arrow
                    ) { return; }
                    const { x, y } = opt.absolutePointer;
                    arrow.setPosition(x, y);
                    func({ x, y });
                });
            },
        };
        layer.feature = feature;
        return feature;
    };

    applyRectFeature = (layer, color, rotateable, options = {}) => {
        const { drawable = true } = options;
        const state = {
            started: false,
            startX: 0,
            startY: 0,
            rect: null,
            release: false,
            locked: []
        };
        const feature = { type: "rect-feature", color };

        const removeRects = () => {
            layer.getObjects()
                .filter(o => o.feature === feature.type)
                .forEach(o => layer.removeObject(o));
        };
        const lockOthers = () => {
            state.locked = layer.getObjects().filter(o => o.feature !== feature.type);
            state.locked.forEach(o => (o.evented = false));
        };
        const unlockOthers = () => {
            state.locked.forEach(o => (o.evented = true));
            state.locked = [];
        };
        const cleanup = () => {
            unlockOthers();
            state.started = false;
            state.release = false;
            state.startX = 0;
            state.startY = 0;
        };
        const start = ({ x, y }) => {
            if (state.started || this.canvas.getActiveObject()) {
                return;
            }
            removeRects();
            lockOthers();
            state.rect = this.addRect(layer, x, y, 0, 0, rotateable, false, {
                feature: feature.type,
                fill: color,
                selectable: true,
                evented: true,
                hasControls: true
            });
            state.started = true;
            state.startX = x;
            state.startY = y;
        };
        const update = ({ x, y }) => {
            if (!state.started) {
                return;
            }
            const w = x - state.startX;
            const h = y - state.startY;
            state.rect.set({
                left: w < 0 ? x : state.startX,
                top: h < 0 ? y : state.startY,
                width: Math.abs(w),
                height: Math.abs(h)
            });
            this.canvas.renderAll();
        };
        const finish = () => {
            if (!state.started) {
                return;
            }
            this.canvas.setActiveObject(state.rect);
            this.canvas.renderAll();
            cleanup();
        };
        let selectFunction;
        feature.onSelect = func => {
            selectFunction = func;
            const selectionHandler = opt => {
                const [selected] = opt.selected ?? [];
                if (state.rect && selected !== state.rect) {
                    removeRects();
                }
                func(selected);
            };
            layer.setHandler("selection:created", selectionHandler);
            layer.setHandler("selection:updated", selectionHandler);
        };
        if (drawable) {
            layer.setHandler("mouse:down:before", () => {
                if (this.canvas.getActiveObject()) {
                    state.release = true;
                }
            });
            layer.setHandler("mouse:down", opt => {
                if (opt.e.which !== this.leftClick) {
                    return;
                }
                if (!opt.target && selectFunction) {
                    selectFunction(undefined);
                }
                if (state.release) {
                    if (state.rect !== this.canvas.getActiveObject()) {
                        removeRects();
                    }
                    state.release = false;
                    return;
                }
                const { x, y } = opt.absolutePointer;
                if (state.started) {
                    finish();
                } else {
                    start({ x, y });
                }
            });
            layer.setHandler("mouse:move", opt => {
                update(opt.absolutePointer);
            });
        }
        feature.onModify = func => {
            layer.setHandler("object:modified", opt => func(opt.target));
        };

        //INFO: the region is the rect's own path, the stroke is drawn around it and ignored here
        const getGeometry = rect => ({
            left: rect.left,
            top: rect.top,
            width: rect.width * rect.scaleX,
            height: rect.height * rect.scaleY
        });

        feature.getRegion = () => (state.rect ? getGeometry(state.rect) : undefined);
        feature.placeRect = geometry => {
            removeRects();
            const rect = this.addRect(layer, geometry.left, geometry.top,
                geometry.width, geometry.height, rotateable, false, {
                    feature: feature.type,
                    fill: "transparent",
                    stroke: color,
                    strokeWidth: Math.max(1, Math.round(2 / this.canvas.getZoom())),
                    hasBorders: false,
                    cornerColor: color,
                    cornerStrokeColor: color,
                    lockScalingFlip: true,
                    selectable: true,
                    evented: true,
                    hasControls: true
                });
            rect.setCoords();
            state.rect = rect;
            rect.on("removed", () => {
                if (state.rect === rect) {
                    state.rect = null;
                }
            });
            this.canvas.setActiveObject(rect);
            this.canvas.renderAll();
            return rect;
        };

        layer.feature = feature;
        return feature;
    };

    applyLineFeature = (layer, color, single, useArrows = false) => {
        const mouseDown = (opt, obj) => {
            if (opt.e.which !== this.leftClick) {
                return;
            }
            if (!obj.started) {
                if (this.canvas.getActiveObject()) {
                    return;
                }
                if (single) {
                    layer.getObjects().forEach(object => {
                        if (object.feature === feature.type) {
                            layer.removeObject(object);
                        }
                    });
                }
                const { x, y } = opt.absolutePointer;
                obj.line = this.addLine(layer, x, y, x, y, true, { stroke: color });
                if (useArrows) {
                    obj.start = this.addRect(layer, x, y, 2, 12, false, true, { fill: color, originX: "center", originY: "center", opacity: 0, selectable: false, evented: false });
                    obj.end = this.addTriangle(layer, x, y, 8, 12, false, true, { fill: color, originX: "center", originY: "center" });
                } else {
                    obj.start = this.addRect(layer, x, y, 2, 12, false, true, { fill: color, originX: "center", originY: "center" });
                    obj.end = this.addRect(layer, x, y, 2, 12, false, true, { fill: color, originX: "center", originY: "center" });
                }
                //Workarround to keep the line visible even if parts of it are off-screen
                obj.line.isOnScreen = () => true;
                obj.start.isOnScreen = () => true;
                obj.end.isOnScreen = () => true;

                obj.text = this.addText(layer, x, y, true, { fill: color, originX: "center" });
                obj.started = true;
            } else {
                const group = new Group([obj.line, obj.start, obj.end, obj.text]);
                layer.addObject(group);
                group.set({ feature: feature.type });
                this.canvas.setActiveObject(group);
                Object.keys(obj).forEach(key => (obj[key] = undefined));
                feature.placeFunction?.(group);
            }
        };
        const mouseMove = (opt, obj) => {
            if (!obj.started) {
                return;
            }
            const { line, start, end, text } = obj;
            const { x, y } = opt.absolutePointer;
            const angleDeg = Math.atan2(line.y2 - line.y1, line.x2 - line.x1) * 180 / Math.PI;
            const flipText = angleDeg < -90 || angleDeg > 90;
            line.set({ x2: x, y2: y });
            end.set({ left: x, top: y });
            text.set({
                left: line.getCenterPoint().x + 1,
                top: line.getCenterPoint().y + 1,
                flipY: flipText,
                flipX: flipText
            });
            if (feature.setText) {
                const diffX = line.x2 - line.x1;
                const diffY = line.y2 - line.y1;
                const angleDeg = ((Math.atan2(diffX, diffY) * 180 / Math.PI - 180)) * -1;
                const distance = Math.hypot(diffX, diffY) * this.refScale;
                text.set({ text: feature.setText(distance, angleDeg) });
            }
            [text, start, end].forEach(obj => {
                const rotationOffset = obj.type === "triangle" ? 90 : 0;
                obj.rotate(angleDeg + rotationOffset);
            });
            this.canvas.renderAll();
        };

        const obj = {};
        layer.setHandler("mouse:down", opt => mouseDown(opt, obj));
        layer.setHandler("mouse:move", opt => mouseMove(opt, obj));

        const feature = {
            type: "rect-feature",
            color: color,
            setText: undefined,
            placeFunction: undefined,
            onSetText: func => {
                feature.setText = func;
            },
            onSelect: func => {
                layer.setHandler("selection:created", opt => func(opt.selected[0]));
                layer.setHandler("selection:updated", opt => func(opt.selected[0]));
                layer.setHandler("selection:cleared", () => func(undefined));
            },
            onModify: func => {
                layer.setHandler("object:modified", opt => func(opt.target));
            },
            onPlace: func => {
                feature.placeFunction = func;
            },
        };
        layer.feature = feature;
        return feature;
    };

    applyCircleFeature = (layer, color) => {
        const mouseDown = opt => {
            if (opt.e.which !== this.leftClick) {
                return;
            }
            if (this.canvas.getActiveObject()) {
                return;
            }
            layer.getObjects().forEach(object => {
                if (object.feature === feature.type) {
                    layer.removeObject(object);
                }
            });
            const { x, y } = opt.absolutePointer;
            const circle = this.addCircle(layer, x, y, true, {
                feature: feature.type,
                selectable: true,
                evented: true,
                fill: color,
            });
            this.canvas.setActiveObject(circle);
        };
        layer.setHandler("mouse:down", opt => mouseDown(opt));

        const feature = {
            type: "circle-feature",
            color: color,
            onSelect: func => {
                layer.setHandler("selection:created", opt => func(opt.selected[0]));
                layer.setHandler("selection:updated", opt => func(opt.selected[0]));
                layer.setHandler("selection:cleared", () => func(undefined));
            },
            onModify: func => {
                layer.setHandler("object:modified", opt => func(opt.target));
            },
        };
        layer.feature = feature;
        return feature;
    };

    // Places a text box, a table box (safety distances), an icon, an icon with a fixed
    // caption, or a dashed square, depending on the shape it is armed with (service-desk#1617).
    applyPlacementFeature = (layer, color) => {
        const state = { armed: undefined };
        const mouseDown = async opt => {
            if (opt.e.which !== this.leftClick) {
                return;
            }
            if (!state.armed || this.canvas.getActiveObject()) {
                return;
            }
            const { x, y } = opt.absolutePointer;
            const { armed } = state;
            state.armed = undefined;
            const initialScale = Math.max(1, 1 / this.canvas.getZoom());
            const properties = {
                feature: feature.type,
                fill: color,
                scaleX: initialScale,
                scaleY: initialScale,
            };
            // string -> wrapping text box; { content } -> table box; { icon, text } -> icon
            // with a fixed caption; { image } -> plain icon; anything else -> dashed square.
            let placed;
            if (typeof armed === "string") {
                placed = this.addTextbox(layer, x, y, armed, properties);
            } else if (armed.content) {
                placed = await this.addTableTextbox(layer, x, y, armed.content, { ...properties, ...armed.properties });
            } else if (armed.icon) {
                placed = await this.addIconTextbox(layer, x, y, armed.icon, armed.text, { ...properties, ...armed.properties });
            } else if (armed.image) {
                placed = await this.addImage(layer, x, y, 0, armed.scale ?? initialScale, armed.opacity ?? 1, armed.image, {
                    originX: "center",
                    originY: "center",
                    hasControls: true,
                    ...properties,
                    ...armed.properties,
                });
            } else {
                // Climbing square: always true to scale, never distorted by the zoom-based
                // initialScale, so placing it needs no resizing afterward.
                placed = this.addRect(layer, x, y, armed.size, armed.size, true, false, {
                    originX: "center",
                    originY: "center",
                    ...properties,
                    scaleX: 1,
                    scaleY: 1,
                    ...armed.properties,
                });
                placed.setControlsVisibility({ ml: false, mr: false, mt: false, mb: false });
            }
            this.canvas.setActiveObject(placed);
            this.canvas.renderAll();
            feature.placeFunction?.(placed);
        };
        layer.setHandler("mouse:down", mouseDown);

        const feature = {
            type: "placement-feature",
            color: color,
            placeFunction: undefined,
            arm: armed => {
                state.armed = armed;
            },
            disarm: () => {
                state.armed = undefined;
            },
            onSelect: func => {
                layer.setHandler("selection:created", opt => func(opt.selected[0]));
                layer.setHandler("selection:updated", opt => func(opt.selected[0]));
                layer.setHandler("selection:cleared", () => func(undefined));
            },
            onModify: func => {
                layer.setHandler("object:modified", opt => func(opt.target));
            },
            onPlace: func => {
                feature.placeFunction = func;
            },
        };
        layer.feature = feature;
        return feature;
    };

    addTextbox = (layer, x, y, text, properties) => {
        const {
            fill = "blue",
            backgroundColor = "white",
            stroke = "blue",
            strokeWidth = 1,
            width = 150,
            fontFamily = "Arial",
            ...groupProperties
        } = properties || {};
        const padding = 4;
        const textbox = new Textbox(text, {
            left: x + padding,
            top: y + padding,
            width,
            fontSize: 10,
            fontFamily,
            fill,
            editable: false,
            originX: "left",
            originY: "top",
            selectable: false,
            evented: false,
        });
        const rect = new Rect({
            left: x,
            top: y,
            width: textbox.width + padding * 2,
            height: textbox.height + padding * 2,
            fill: backgroundColor,
            stroke,
            strokeWidth,
            originX: "left",
            originY: "top",
            selectable: false,
            evented: false,
        });
        const group = new Group([rect, textbox], {
            hasControls: true,
            selectable: true,
            evented: true,
            textboxRef: textbox,
            ...groupProperties,
        });
        layer.addObject(group);
        return group;
    };

    // Like addTextbox, but with a centered title, label/value columns and an optional
    // centered footer. A wrapping Textbox cannot align a value column to the right.
    // An optional content.headerImage (the warning triangle, service-desk#1617) is drawn
    // above the frame, not inside it - the frame itself stays exactly as before.
    addTableTextbox = async (layer, x, y, content, properties) => {
        const {
            fill = "blue",
            backgroundColor = "white",
            stroke = "blue",
            strokeWidth = 1,
            fontFamily = "Arial",
            fontSize = 10,
            ...groupProperties
        } = properties || {};
        const padding = 4;
        const columnGap = 12;
        const { title, rows, footer, headerImage } = content;

        const addTextObject = (text, options) => new FabricText(text, {
            fontSize,
            fontFamily,
            fill,
            fontWeight: "bold",
            originX: "left",
            originY: "top",
            selectable: false,
            evented: false,
            ...options,
        });

        const titleText = addTextObject(title, { originX: "center" });
        const labelTexts = rows.map(([label]) => addTextObject(label));
        const valueTexts = rows.map(([, value]) => addTextObject(value, { originX: "right" }));
        const footerText = footer ? addTextObject(footer, { originX: "center" }) : undefined;

        const labelWidth = Math.max(...labelTexts.map(text => text.width));
        const valueWidth = Math.max(...valueTexts.map(text => text.width));
        const lineHeight = titleText.height;
        const contentWidth = Math.max(
            labelWidth + columnGap + valueWidth,
            titleText.width,
            footerText ? footerText.width : 0
        );
        const lineCount = 1 + rows.length + (footerText ? 1 : 0);

        const boxWidth = contentWidth + padding * 2;
        const boxHeight = lineCount * lineHeight + padding * 2;
        const headerHeight = headerImage ? lineHeight * 3 : 0;
        const boxTop = y + headerHeight;

        const rect = new Rect({
            left: x,
            top: boxTop,
            width: boxWidth,
            height: boxHeight,
            fill: backgroundColor,
            stroke,
            strokeWidth,
            originX: "left",
            originY: "top",
            selectable: false,
            evented: false,
        });

        const centerX = x + boxWidth / 2;
        titleText.set({ left: centerX, top: boxTop + padding });
        labelTexts.forEach((text, index) => text.set({
            left: x + padding,
            top: boxTop + padding + (index + 1) * lineHeight,
        }));
        valueTexts.forEach((text, index) => text.set({
            left: x + boxWidth - padding,
            top: boxTop + padding + (index + 1) * lineHeight,
        }));
        footerText?.set({ left: centerX, top: boxTop + padding + (rows.length + 1) * lineHeight });

        const children = [rect, titleText, ...labelTexts, ...valueTexts];
        if (footerText) {
            children.push(footerText);
        }
        if (headerImage) {
            const header = await FabricImage.fromURL(headerImage);
            header.scaleToHeight(headerHeight);
            header.set({
                left: centerX,
                top: y,
                originX: "center",
                originY: "top",
                selectable: false,
                evented: false,
            });
            children.push(header);
        }
        const group = new Group(children, {
            hasControls: true,
            selectable: true,
            evented: true,
            ...groupProperties,
        });
        layer.addObject(group);
        return group;
    };

    // Icon with a fixed caption placed as one unit (directional-radio symbol, service-desk#1617).
    addIconTextbox = async (layer, x, y, imageBase64, text, properties) => {
        const {
            fill = "blue",
            fontFamily = "Arial",
            fontSize = 10,
            ...groupProperties
        } = properties || {};
        const label = new FabricText(text, {
            fontSize,
            fontFamily,
            fill,
            fontWeight: "bold",
            originX: "left",
            originY: "top",
            selectable: false,
            evented: false,
        });
        const icon = await FabricImage.fromURL(imageBase64);
        icon.scaleToHeight(label.height);
        icon.set({
            left: x,
            top: y,
            originX: "left",
            originY: "top",
            selectable: false,
            evented: false,
        });
        label.set({ left: x + icon.getScaledWidth() + 4, top: y });
        const group = new Group([icon, label], {
            hasControls: true,
            selectable: true,
            evented: true,
            ...groupProperties,
        });
        layer.addObject(group);
        return group;
    };

    addText = (layer, x, y, autoScale, properties) => {
        const text = new FabricText("", {
            textBackgroundColor: "white",
            fontSize: 10,
            left: x,
            top: y,
            ...properties,
        });
        if (autoScale) {
            const rescale = () => {
                const newScale = 1 / this.canvas.getZoom() * 1.6;
                if (newScale > 1) {
                    if (newScale > 1) {
                        text.scaleX = newScale;
                        text.scaleY = newScale;
                    }
                }
            };
            this.canvas.on("rescale", rescale);
            rescale();
        }
        layer.addObject(text);
        return text;
    };

    addCircle = (layer, x, y, autoScale, properties) => {
        const circle = new Circle({
            originX: "center",
            originY: "center",
            radius: 4,
            left: x,
            top: y,
            ...properties,
        });
        if (autoScale) {
            const rescale = () => {
                const newScale = 1 / this.canvas.getZoom();
                if (newScale > 1) {
                    circle.scaleX = newScale;
                    circle.scaleY = newScale;
                }
            };
            this.canvas.on("rescale", rescale);
            rescale();
        }
        layer.addObject(circle);
        return circle;
    };

    addRect = (layer, x, y, width, height, rotateable, autoScale, properties) => {
        const rect = new Rect({
            left: x,
            top: y,
            width: width,
            height: height,
            originX: "left",
            originY: "top",
            hasControls: true,
            ...properties,
        });
        if (rotateable === false) {
            rect.setControlsVisibility({ mtr: false });
        }
        if (autoScale) {
            const rescale = () => {
                const newScale = 1 / this.canvas.getZoom();
                if (newScale > 1) {
                    rect.set({ scaleX: newScale, scaleY: newScale });
                }
                rect.visible = true;
                this.canvas.renderAll();
            };
            this.canvas.on("rescale", rescale);
            rescale();
        }
        if (properties.stroke && properties.strokeUniform) {
            const rescaleStroke = () => {
                const zoom = this.canvas.getZoom();
                const newStrokeWidth = Math.max(1, Math.floor(1 / zoom / 1.8));
                rect.strokeWidth = newStrokeWidth;
                this.canvas.renderAll();
            };
            rescaleStroke();
            this.canvas.on("rescale", rescaleStroke);
        }
        layer.addObject(rect);
        return rect;
    };

    addTriangle = (layer, x, y, width, height, rotateable, autoScale, properties) => {
        const triangle = new Triangle({
            left: x,
            top: y,
            width: width,
            height: height,
            originX: "left",
            originY: "top",
            hasControls: true,
            ...properties,
        });
        if (rotateable === false) {
            triangle.setControlsVisibility({ mtr: false });
        }
        if (autoScale) {
            const rescale = () => {
                const newScale = 1 / this.canvas.getZoom();
                if (newScale > 1) {
                    triangle.set({ scaleX: newScale, scaleY: newScale });
                }
                triangle.visible = true;
                this.canvas.renderAll();
            };
            this.canvas.on("rescale", rescale);
            rescale();
        }
        if (properties.stroke && properties.strokeUniform) {
            const rescaleStroke = () => {
                const zoom = this.canvas.getZoom();
                const newStrokeWidth = Math.max(1, Math.floor(1 / zoom / 1.8));
                triangle.strokeWidth = newStrokeWidth;
                this.canvas.renderAll();
            };
            rescaleStroke();
            this.canvas.on("rescale", rescaleStroke);
        }
        layer.addObject(triangle);
        return triangle;
    };

    addLine = (layer, x1, y1, x2, y2, autoScale, properties) => {
        const line = new Line([x1, y1, x2, y2], {
            originX: "center",
            originY: "center",
            ...properties,
        });
        if (autoScale) {
            const rescale = () => {
                const newScale = 1 / this.canvas.getZoom();
                if (newScale > 1) {
                    line.set({ strokeWidth: newScale });
                }
            };
            this.canvas.on("rescale", rescale);
            rescale();
        }
        layer.addObject(line);
        return line;
    };

    addArrow = (layer, key, x, y, direction, autoScale, properties) => {
        const calc = (direction, x, y, scale) => {
            const angle = direction - 90;
            const lineLength = 25 * scale;
            const angleInRadians = angle * Math.PI / 180;
            const endX = x + lineLength * Math.cos(angleInRadians);
            const endY = y + lineLength * Math.sin(angleInRadians);
            return [endX, endY, angle];
        };
        const [endX, endY, angle] = calc(direction, x, y, 1);
        const circle = new Circle({
            left: x,
            top: y,
            fill: properties.color,
            radius: 4,
            originX: "center",
            originY: "center",
        });
        const line = new Line([x, y, endX, endY], {
            originX: "center",
            originY: "center",
            stroke: properties.color,
        });
        const triangle = new Triangle({
            left: endX,
            top: endY,
            angle: angle - 270,
            width: 7,
            height: 15,
            fill: properties.color,
            originX: "center",
            originY: "center",
        });
        const arrow = new Group([circle, line, triangle], {
            key,
            direction,
            ...properties,
        });
        arrow.getPosition = () => {
            const [circle] = arrow.getObjects();
            const groupMatrix = arrow.calcTransformMatrix();
            const relativePosition = new Point(circle.left, circle.top);
            return relativePosition.transform(groupMatrix);
        };
        arrow.setPosition = (x, y) => {
            const absolutePosition = arrow.getPosition(arrow);
            const offsetX = absolutePosition.x - arrow.left;
            const offsetY = absolutePosition.y - arrow.top;
            arrow.set({
                left: x - offsetX,
                top: y - offsetY
            });
            arrow.setCoords();
            this.canvas.setActiveObject(arrow);
        };
        if (autoScale) {
            const rescale = () => {
                const newScale = 1 / this.canvas.getZoom();
                if (newScale > 1) {
                    const [circle, line, triangle] = arrow.getObjects();
                    const [endX, endY] = calc(direction, circle.left, circle.top, newScale);
                    circle.set({
                        radius: 4 * newScale
                    });
                    line.set({
                        strokeWidth: 1 * Number(newScale),
                        x1: circle.left,
                        y1: circle.top,
                        x2: endX,
                        y2: endY
                    });
                    triangle.set({
                        width: 7 * newScale,
                        height: 15 * newScale,
                        left: endX,
                        top: endY
                    });
                    arrow.add();
                }
            };
            this.canvas.on("rescale", rescale);
            rescale();
        }
        layer.addObject(arrow);
        return arrow;
    };

    // Returns both endpoints of a line in absolute canvas coordinates, accounting for any
    // move/rotate/resize applied to the group it sits in since it was built (service-desk#1668).
    // Line.x1..y2 stay in the coordinates the line was created with, so they must not be
    // transformed by the group matrix - calcLinePoints gives them relative to the line centre.
    getLineEndpoints = line => {
        const { x1, y1, x2, y2 } = line.calcLinePoints();
        const matrix = line.calcTransformMatrix();
        return [new Point(x1, y1).transform(matrix), new Point(x2, y2).transform(matrix)];
    };

    addImage = (layer, x, y, angle, scale, opacity, imageSrc, properties) =>
        new Promise((resolve, reject) => {
            try {
                FabricImage.fromURL(imageSrc).then(image => {
                    image.set({
                        left: x,
                        top: y,
                        angle,
                        opacity: opacity,
                        scaleX: scale,
                        scaleY: scale,
                        ...properties
                    });
                    layer.addObject(image);
                    image.setCoords();
                    resolve(image);
                }).catch(error => {
                    reject(error);
                });
            } catch (e) {
                reject(e);
            }
        });

    zoom = zoom => this.canvas.setZoom(zoom);

    addColorBar = async colorBarData => {
        const layer = Array.from(this.layers.values())
            .find(layer => layer.active);
        const colorBarName = layer.name + "-colorbar";
        const colorBarImage = await FabricImage.fromURL(colorBarData.value);
        const textParams = {
            fontSize: 10,
            fill: "black",
            backgroundColor: "#fff",
        };
        const topText = new FabricText(colorBarData.max.toFixed(2), textParams);
        const buttomText = new FabricText(colorBarData.min.toFixed(2), textParams);
        const colorBarGroup = new Group([colorBarImage, topText, buttomText], { colorBarName });
        colorBarImage.set({
            top: -237,
            scaleX: 0.6,
            scaleY: 0.91,
            strokeWidth: 0.5,
            stroke: "#898989",
        });
        buttomText.set({ top: 218 });
        layer.addObject(colorBarGroup, 100);

        const updateColorBarGroup = () => {
            if (this.canvas.getObjects().includes(colorBarGroup) === false) {
                return;
            }
            const vpt = this.canvas.viewportTransform;
            const [scaleX, , , scaleY, translateX, translateY] = vpt;
            const imageScale = this.canvas.height / (colorBarGroup.height - 7) / scaleY;
            if (this.canvas.width !== 0) {
                const posX = (-translateX + 5) / scaleX;
                const posY = (-translateY) / scaleY;
                colorBarGroup.set({
                    left: posX,
                    top: posY,
                    scaleX: imageScale,
                    scaleY: imageScale,
                });
                colorBarGroup.setCoords();
                this.canvas.bringObjectToFront(colorBarGroup);
            }
        };
        this.canvas.on("viewport", updateColorBarGroup);
        updateColorBarGroup();
        this.canvas.renderAll();
    };

    removeColorBar = () => {
        const layer = Array.from(this.layers.values())
            .find(layer => layer.active);
        if (layer === undefined) {
            return;
        }
        const colorBarName = layer.name + "-colorbar";
        layer.getObjects()
            .filter(obj => obj.colorBarName === colorBarName)
            .forEach(colorBarObject => {
                layer.removeObject(colorBarObject);
            });
        this.canvas.renderAll();
    };

    toPngDataUrl = rect => {
        this.canvas.discardActiveObject();
        // const ratio = window.devicePixelRatio;
        const ratio = 1;
        const params = {
            format: "png",
            quality: 0.8,
        };
        if (rect) {
            const { tl, tr, bl } = rect.oCoords;
            Array.from(this.layers.values())
                .find(layer => layer.active)
                .removeObject(rect);
            this.canvas.renderAll();
            params.top = tl.y;
            params.left = tl.x;
            params.width = (tr.x - tl.x) / ratio;
            params.height = (bl.y - tl.y) / ratio;
        } else {
            params.top = 0;
            params.left = 0;
            params.width = this.canvas.width / ratio;
            params.height = this.canvas.height / ratio;
        }
        return this.canvas.toDataURL(params);
    };

    rotatePoint = (x, y, pointX, pointY, angle) => {
        const radianAngle = angle * (Math.PI / 180);
        const rcos = Math.cos(radianAngle);
        const rsin = Math.sin(radianAngle);
        const translatedX = x - pointX;
        const translatedY = y - pointY;
        const rotatedX = translatedX * rcos - translatedY * rsin;
        const rotatedY = translatedY * rcos + translatedX * rsin;
        const finalX = rotatedX + pointX;
        const finalY = rotatedY + pointY;
        return { x: finalX, y: finalY };
    };

    applyStrokeWithoutTranslation = (object, stroke, strokeWidth) => {
        const oldTop = object.get("top") ?? 0;
        const oldLeft = object.get("left") ?? 0;
        const oldStrokeWidth = object.get("strokeWidth") ?? 0;
        const angle = object.get("angle") ?? 0;
        const offset = Math.abs(strokeWidth - oldStrokeWidth) / 2;
        const angleCounterclockwise = 360 - angle;
        const translationAngleRadians = ((45 + angleCounterclockwise + (strokeWidth >= oldStrokeWidth ? 0 : 180)) % 360) / 180 * Math.PI;
        const totalTranslationLength = Math.sqrt(2 * offset * offset);
        const translationY = totalTranslationLength * Math.sin(translationAngleRadians);
        const translationX = totalTranslationLength * Math.cos(translationAngleRadians);
        const top = oldTop + translationY;
        const left = oldLeft - translationX;
        object.set({
            top,
            left,
            stroke,
            strokeWidth,
            strokeUniform: true,
        });
    };

    //svg format: M=move, L=line, Z=close)
    createSvgPath = paths => {
        let pathString = "";
        paths.forEach(path => {
            path.forEach((pt, index) => {
                pathString += (index === 0 ? "M " : "L ") + pt.x + " " + pt.y + " ";
            });
            pathString += "Z ";
        });
        return pathString;
    };

    //svg format: M=move, L=line, Z=close)
    pathFromSvg = path => {
        const polygons = [];
        let polygon = [];
        for(const instruction of path.path) {
            if(instruction[0] === "Z") {
                polygons.push(polygon);
                continue;
            }

            if(instruction[0] === "M") {
                polygon = [];
            }

            const point = {x: instruction[1], y: instruction[2]};
            polygon.push(point);
        }
        return polygons;
    };
}